/**
 * EN 16931 compliant calculation utilities.
 * Mirrors Calculations::TotalsCalculatorService (Ruby) exactly.
 *
 * Two mandatory rounding points, both computed on exact decimals (see "Exact
 * line arithmetic" below), never on floating-point products:
 *   BT-131  line net     = calculateItemNet — (unitPrice − discountPerUnit) × quantity, rounded once
 *   BT-117  category tax = calculateCategoryTax — multiplyAndRound(categoryTaxable, rate, currencyDecimals)
 *
 * An expense may be calculated the other way round (calculateTotals, mode
 * "gross_per_rate"): the line grosses (calculateItemGrossLine) are summed per
 * rate and the VAT is carved out of the sum (carveTax), so the printed gross of
 * a till receipt is kept to the cent. Documents are always net_per_rate.
 */

import { getCurrencyDecimals } from "./number-format.util"

export const UNIT_PRICE_MAXIMUM_FRACTION_DIGITS = 6

// How an expense's VAT is calculated (Expense#vat_calculation_mode); an issued document is always per rate from the net.
export const NET_PER_RATE = "net_per_rate"
export const GROSS_PER_RATE = "gross_per_rate"

/**
 * Financial rounding to 2 decimal places ("round half away from zero").
 * Thin alias for the currency-aware `roundToDecimals` — see its docs.
 */
export function roundTo2(value) {
  return roundToDecimals(value, 2)
}

/**
 * Financial rounding to N decimal places ("round half away from zero").
 * Currency-aware — pass `getCurrencyDecimals(currency)` for currency-driven
 * precision (0 for VND/JPY, 2 for EUR, 3 for BHD).
 *
 * Solves two IEEE 754 issues:
 *   1. Math.round(1.005 * 100) / 100 = 1.00 (wrong: should be 1.01)
 *      Using string concat with 'eN' forces JS to parse via scientific notation.
 *   2. Math.round(-100.5) = -100 (rounds toward +∞), but financial rounding
 *      needs -100.5 → -101 (away from zero). We negate, round positive, negate back.
 *
 * The two guards below must stay in this order — they keep any value the string
 * trick cannot express from reaching it. `Number("1e-13" + "e2")` is NaN, and a
 * single NaN then infects every downstream subtotal, VAT row and total.
 */
export function roundToDecimals(value, decimals) {
  // Coerce first: the trick below is string concatenation, so a numeric string
  // ("100.555") used to round correctly by accident. Keep that working — turning
  // it into 0 would be silent wrong money, worse than the NaN this guard removes.
  const amount = Number(value)

  // NaN, ±Infinity and anything non-numeric ("100 eur", null, {}) stop here and
  // never reach `.toString()`, so neither a TypeError nor a false "e" match on
  // text is possible. Money that cannot be computed is 0, not a NaN.
  if (!Number.isFinite(amount)) return 0

  if (Math.abs(amount).toString().includes("e")) {
    // JS switches to scientific notation below 1e-6 and at/above 1e21, and the
    // string trick yields NaN at both ends. Below 1e-6 the value is float noise
    // from proportional discount splits (e.g. -1.1368683772161603e-13) and
    // rounds to 0 for every currency precision we use (0-6 decimals). At/above
    // 1e21 doubles are already integral, so rounding is a no-op.
    return Math.abs(amount) < 1 ? 0 : amount
  }

  if (amount >= 0) {
    return Number(Math.round(amount + "e" + decimals) + "e-" + decimals)
  }
  return -Number(Math.round(-amount + "e" + decimals) + "e-" + decimals)
}

// BT-114 — per-currency cash rounding granularity map.
// Mirrors CurrencyEnums::CASH_ROUNDING_GRANULARITY (Ruby).
// EUR/CZK/HUF retired small-denomination coins; all other currencies default
// to 1 minor unit (1 / 10^decimals), which is a no-op for standard 2dp codes.
const CASH_GRANULARITY_MAP = {
	EUR: 0.05,
	CZK: 1,
	HUF: 5
}

/**
 * Round `amount` to the per-currency cash granularity (BT-114).
 * Mirrors Ruby Document#round_to_cash_granularity exactly.
 *
 * Uses Math.round (half-up for positive payable amounts) then cleans up
 * IEEE 754 residue via roundToDecimals — same two-step pattern as Ruby:
 *   ((amount / g).round * g).round(currency_decimals)
 *
 * Parity test vectors (same as Task-1 Ruby suite):
 *   12.37,   "EUR" → 12.35  (5-cent coin granularity)
 *   1237.40, "CZK" → 1237   (1-crown granularity, no-op decimal residue)
 *   12.37,   "USD" → 12.37  (1-cent = no-op for standard 2dp currency)
 */
export function roundToCashGranularity(amount, currency) {
	const decimals = getCurrencyDecimals(currency)
	const granularity = CASH_GRANULARITY_MAP[currency?.toUpperCase()] ?? 1 / 10 ** decimals
	if (granularity === 0) return amount
	return roundToDecimals(Math.round(amount / granularity) * granularity, decimals)
}

// --- Exact line arithmetic ---------------------------------------------------
//
// The line net is money the server stores (ItemCalculatable, BigDecimal), so
// the preview computes it the same way: on exact decimals, never on Numbers.
// A float port of the server formula still misses a cent in ~1 of 700 cases
// (292.85 × 15 at −6 % gives 4129.18, the server 4129.19). A decimal is
// { coef: BigInt, scale } — the value coef / 10^scale.
//
// Numbers only: every exported function below takes JavaScript Numbers (what
// parseLocaleNumber returns) and throws a TypeError on anything else. A
// localized string ("12,5") would otherwise read as 0 — a silent zero discount
// or net instead of an error. NaN and ±Infinity are Numbers and count as 0.

// A Number's exact decimal: the shortest string that round-trips to it, which
// is the number the user typed ("292.85", not the double 292.8499999…). Also
// reads JS's exponent forms "1e-7", "1.5e+21".
function toDecimal(value) {
  if (typeof value !== "number") throw new TypeError(`expected a Number, got ${typeof value} ${String(value)}`)
  if (!Number.isFinite(value)) return { coef: 0n, scale: 0 }
  const [, sign, whole, fraction = "", exponent = "0"] = /^(-?)(\d+)(?:\.(\d+))?(?:e([+-]\d+))?$/.exec(String(value))
  const coef = BigInt(sign + whole + fraction)
  const scale = fraction.length - Number(exponent)
  return scale < 0 ? { coef: coef * 10n ** BigInt(-scale), scale: 0 } : { coef, scale }
}

const HUNDRED = { coef: 100n, scale: 0 }

function multiplyDecimals(a, b) {
  return { coef: a.coef * b.coef, scale: a.scale + b.scale }
}

function subtractDecimals(a, b) {
  const scale = Math.max(a.scale, b.scale)
  return {
    coef: a.coef * 10n ** BigInt(scale - a.scale) - b.coef * 10n ** BigInt(scale - b.scale),
    scale
  }
}

function addDecimals(a, b) {
  return subtractDecimals(a, { coef: -b.coef, scale: b.scale })
}

// Ruby's BigDecimal#round default: half away from zero, negatives included.
function roundDecimal({ coef, scale }, decimals) {
  if (scale <= decimals) return { coef, scale }
  const divisor = 10n ** BigInt(scale - decimals)
  const magnitude = coef < 0n ? -coef : coef
  let quotient = magnitude / divisor
  if ((magnitude % divisor) * 2n >= divisor) quotient += 1n
  return { coef: coef < 0n ? -quotient : quotient, scale: decimals }
}

// Back to the Number the rest of the code works with. A value rounded to at
// most 6 decimals reads back as the double nearest to it, i.e. the same
// Number a user typing that amount would produce.
function toNumber({ coef, scale }) {
  const digits = (coef < 0n ? -coef : coef).toString().padStart(scale + 1, "0")
  const text = scale === 0 ? digits : `${digits.slice(0, -scale)}.${digits.slice(-scale)}`
  const number = Number((coef < 0n ? "-" : "") + text)
  return number === 0 ? 0 : number
}

// ItemCalculatable#clamp_unit_price_precision: the server computes on prices
// and absolute discounts already rounded to UNIT_PRICE_MAXIMUM_FRACTION_DIGITS.
function toUnitPriceDecimal(value) {
  return roundDecimal(toDecimal(value), UNIT_PRICE_MAXIMUM_FRACTION_DIGITS)
}

/**
 * Per-unit discount, as ItemCalculatable#discount_per_unit: an absolute amount
 * wins when it is > 0; otherwise a percentage > 0 of the unit price, rounded to
 * UNIT_PRICE_MAXIMUM_FRACTION_DIGITS; otherwise 0. Signed like the server: a
 * percentage of a NEGATIVE unit price is negative (−19 at 10 % → −1.9).
 * Numbers only (throws a TypeError otherwise).
 */
export function discountPerUnit(unitPrice, { percent = 0, amount = 0 } = {}) {
  const absolute = toUnitPriceDecimal(amount)
  if (absolute.coef > 0n) return toNumber(absolute)
  const rate = toDecimal(percent)
  if (rate.coef <= 0n) return 0

  const product = multiplyDecimals(toUnitPriceDecimal(unitPrice), rate)
  return toNumber(roundDecimal({ coef: product.coef, scale: product.scale + 2 }, UNIT_PRICE_MAXIMUM_FRACTION_DIGITS))
}

/**
 * BT-131: Line net amount, as ItemCalculatable#subtotal_after_discount —
 * (unitPrice − discountPerUnit) × quantity in exact decimals, rounded once,
 * half away from zero, to currency precision. The sign comes from the
 * formula: a credit-note line (negative quantity) keeps its discount.
 * Numbers only (throws a TypeError otherwise).
 */
export function calculateItemNet(quantity, unitPrice, discountPerUnit = 0, decimals = 2) {
  const perUnitNet = subtractDecimals(toUnitPriceDecimal(unitPrice), toUnitPriceDecimal(discountPerUnit))
  return toNumber(roundDecimal(multiplyDecimals(perUnitNet, toDecimal(quantity)), decimals))
}

/**
 * Line amount before its discount, as ItemCalculatable#subtotal_before_discount:
 * unitPrice × quantity, exact, rounded once to currency precision.
 * Numbers only (throws a TypeError otherwise).
 */
export function calculateItemSubtotal(quantity, unitPrice, decimals = 2) {
  return calculateItemNet(quantity, unitPrice, 0, decimals)
}

/**
 * A line's discount total, discountPerUnit × quantity rounded to currency
 * precision — the figure the PDF prints in the discount row. Signed.
 * Numbers only (throws a TypeError otherwise).
 */
export function calculateItemDiscount(quantity, discountPerUnit, decimals = 2) {
  return toNumber(roundDecimal(multiplyDecimals(toUnitPriceDecimal(discountPerUnit), toDecimal(quantity)), decimals))
}

/**
 * A line net plus its own VAT, as ItemCalculatable#total_before_discount /
 * #total_after_discount: net + round(net × rate / 100). Display only — the
 * document's VAT is per rate (calculateCategoryTax). Numbers only (throws a
 * TypeError otherwise).
 */
export function calculateItemGross(net, taxRate, decimals = 2) {
  const amount = toDecimal(net)
  return toNumber(roundDecimal(addDecimals(amount, percentOf(amount, toDecimal(taxRate || 0), decimals)), decimals))
}

/**
 * The line gross of a gross_per_rate expense, as ExpenseItem#gross_total_after_discount:
 * the VAT-inclusive price of one unit less its percentage discount, times the
 * quantity, rounded once, half away from zero, to currency precision. The
 * printed `grossUnitPrice` is the source; a line without one takes it from the
 * net, `unitPrice` with the rate on top, and one with neither prices at 0. Both
 * prices and the discount per unit are six-decimal unit prices, like the stored
 * ones. Gross mode takes percentage discounts only, so there is no amount.
 * Numbers only (throws a TypeError otherwise).
 *
 * @param {{ unitPrice?: number, grossUnitPrice?: number, quantity: number, taxRate: number,
 *   discountPercentage?: number }} line
 * @returns {number} the gross of the line after its discount
 */
export function calculateItemGrossLine(
  { unitPrice, grossUnitPrice, quantity, taxRate, discountPercentage = 0 },
  decimals = 2
) {
  const price = effectiveGrossUnitPrice(unitPrice, grossUnitPrice, taxRate)
  // The same formula as the line net, on the gross price: (price − discount per unit) × quantity.
  return calculateItemNet(quantity, price, discountPerUnit(price, { percent: discountPercentage }), decimals)
}

// ExpenseItem#effective_gross_unit_price: the stored gross when there is one
// (a gross of 0 is one), else the net times (100 + rate) / 100.
function effectiveGrossUnitPrice(unitPrice, grossUnitPrice, taxRate) {
  if (grossUnitPrice !== undefined && grossUnitPrice !== null) return toNumber(toUnitPriceDecimal(grossUnitPrice))
  if (unitPrice === undefined || unitPrice === null) return 0

  return grossFromNet(unitPrice, taxRate)
}

/**
 * The VAT-inclusive unit price of a net one, as ExpenseItem#gross_from_net: the net with the rate
 * on top, net × (100 + rate) / 100 in exact decimals, rounded half away from zero to the six
 * decimals of a unit price. A blank rate is 0 %. Numbers only (throws a TypeError otherwise).
 */
export function grossFromNet(unitPrice, taxRate = 0) {
  const percentWithVat = addDecimals(HUNDRED, toDecimal(taxRate || 0))
  return toNumber(percentOf(toUnitPriceDecimal(unitPrice), percentWithVat, UNIT_PRICE_MAXIMUM_FRACTION_DIGITS))
}

/**
 * The net unit price a VAT-inclusive one implies, as ExpenseItem#net_from_gross: the gross taken
 * back out of its rate, gross × 100 / (100 + rate) in exact decimals (a float quotient misses the
 * ties on the seventh decimal), rounded half away from zero to the six decimals of a unit price.
 * The gross is a unit price itself, so it is read at six decimals first, as the model does. A blank
 * rate is 0 %. A rate of −100 % or below has no net for any gross (0). Numbers only (throws a
 * TypeError otherwise).
 */
export function netFromGross(grossUnitPrice, taxRate = 0) {
  const percentWithVat = addDecimals(HUNDRED, toDecimal(taxRate || 0))
  if (percentWithVat.coef <= 0n) return 0

  return multiplyDivideAndRound(
    toUnitPriceDecimal(grossUnitPrice),
    HUNDRED,
    percentWithVat,
    UNIT_PRICE_MAXIMUM_FRACTION_DIGITS
  )
}

/**
 * amount ÷ divisor in exact decimals, rounded once, half away from zero, to `decimals`: a line's
 * unit price from its total (a gross of 5,00 over 3 units, to six decimals). A division by zero is
 * 0, never a NaN. Numbers only (throws a TypeError otherwise).
 */
export function divideAndRound(amount, divisor, decimals = 2) {
  return multiplyDivideAndRound(toDecimal(amount), { coef: 1n, scale: 0 }, toDecimal(divisor), decimals)
}

/**
 * amount × rate / 100 in exact decimals, rounded once, half away from zero, to
 * `decimals` — BigDecimal's `(amount * rate / 100).round(decimals)`. The VAT of
 * a net at a rate; a float product misses a cent (4.10 × 15 % gives 0.61, the
 * server 0.62). Numbers only (throws a TypeError otherwise).
 */
export function multiplyAndRound(amount, rate, decimals = 2) {
  return toNumber(percentOf(toDecimal(amount), toDecimal(rate), decimals))
}

function percentOf(amount, rate, decimals) {
  const product = multiplyDecimals(amount, rate)
  return roundDecimal({ coef: product.coef, scale: product.scale + 2 }, decimals)
}

/**
 * amount × rate / (100 + rate) in exact decimals, rounded once, half away from
 * zero, to `decimals` — BigDecimal's `(amount * rate / (100 + rate)).round(decimals)`.
 * The VAT contained in a VAT-inclusive amount (gross_per_rate carves it out of the
 * rate's gross sum). The quotient is exact, not a float: 0.70 at 12 % is 0.075 on
 * the dot and rounds up to 0.08, a float product gives 0.0749999… and 0.07. A rate
 * of −100 % has no VAT to carve (0). Numbers only (throws a TypeError otherwise).
 */
export function carveTax(amount, rate, decimals = 2) {
  const percent = toDecimal(rate)
  return multiplyDivideAndRound(toDecimal(amount), percent, addDecimals(HUNDRED, percent), decimals)
}

// a × b ÷ c in exact decimals, rounded once, half away from zero, to `decimals`.
// A division by zero is 0: money that cannot be computed is 0, never a NaN.
function multiplyDivideAndRound(a, b, c, decimals) {
  if (c.coef === 0n) return 0
  const numerator = a.coef * b.coef * 10n ** BigInt(c.scale + decimals)
  const denominator = c.coef * 10n ** BigInt(a.scale + b.scale)
  return toNumber({ coef: roundedQuotient(numerator, denominator), scale: decimals })
}

/**
 * numerator ÷ denominator as a whole number, half away from zero (the quotient
 * of an exact division, rounded the way BigDecimal#round rounds it). BigInts
 * in, BigInt out; the sign is the quotient's, whichever operand carries it. A
 * zero denominator throws, as BigInt division does.
 */
export function roundedQuotient(numerator, denominator) {
  const negative = numerator < 0n !== denominator < 0n
  const dividend = numerator < 0n ? -numerator : numerator
  const divisor = denominator < 0n ? -denominator : denominator
  const quotient = (2n * dividend + divisor) / (2n * divisor)
  return negative ? -quotient : quotient
}

/**
 * BT-116 + BT-117: Category-level tax computation.
 * Groups items by rate, computes tax once per group (not per line).
 *
 * @param {Array<{net: number, taxRate: number}>} items - per-line net and rate
 * @returns {{ totalTax: number, summaries: Array<{rate: number, taxable: number, tax: number}> }}
 */
export function calculateCategoryTax(items, decimals = 2) {
  const byRate = new Map()
  for (const { net, taxRate } of items) {
    byRate.set(taxRate, (byRate.get(taxRate) || 0) + net)
  }
  const summaries = []
  let totalTax = 0
  for (const [rate, taxable] of byRate) {
    const roundedTaxable = roundToDecimals(taxable, decimals)
    const tax = multiplyAndRound(roundedTaxable, Number(rate) || 0, decimals)
    summaries.push({ rate, taxable: roundedTaxable, tax })
    totalTax = roundToDecimals(totalTax + tax, decimals)
  }
  return { totalTax, summaries }
}

/**
 * One grouping key per VAT rate: 23, "23", "23.0" and "23,0" are the same rate.
 * Anything that is not a finite number joins the rate-less group (null).
 */
export function rateKey(rate) {
  if (rate === null || rate === undefined) return null
  const number = typeof rate === "string" ? Number(rate.trim().replace(",", ".")) : Number(rate)
  return Number.isFinite(number) ? number : null
}

/**
 * Calculations::LineVatAllocator#call. Per rate, each line's net share of the
 * category taxable total and gross share of its inclusive total are rounded to
 * the currency separately; the last line of the rate absorbs both remainders,
 * and its VAT is gross share − net share. When a rate's nets (or grosses) sum to
 * zero a line keeps its own rounded amount. Signs stay native.
 *
 * @param {Array<{net: number, gross?: number, taxRate: number}>} items line nets BEFORE the document discount;
 *   in gross_per_rate each line's `gross` (calculateItemGrossLine) is the weight of its gross share
 * @param {Array<{rate: number, taxable: number, tax: number}>} summaries calculateCategoryTax after the discount
 * @returns {number[]} one VAT amount per item, in item order
 */
export function allocateLineTaxes(items, summaries, decimals = 2) {
  return allocateLines(items, summaries, decimals).map(({ tax }) => tax)
}

/**
 * allocateLineTaxes with each line's net share as well — the allocator's
 * `shares[:taxable]`, the net after the document discount that the server
 * reads a line's sign from (AdvanceDeduction#line?).
 *
 * The gross weight of a line is the item's `gross` when it has one, as the
 * allocator reads ExpenseItem#total_after_discount, which in gross_per_rate is
 * the printed gross; otherwise its net plus its own rounded VAT.
 *
 * @returns {Array<{taxable: number, tax: number}>} one per item, in item order
 */
export function allocateLines(items, summaries, decimals = 2) {
  const targets = new Map()
  for (const { rate, taxable, tax } of summaries || []) {
    targets.set(rateKey(rate), { taxable, inclusive: roundToDecimals(taxable + tax, decimals) })
  }

  const byRate = new Map()
  items.forEach((item, index) => {
    const key = rateKey(item.taxRate)
    if (!byRate.has(key)) byRate.set(key, [])
    byRate.get(key).push(index)
  })

  const shares = new Array(items.length)
  for (const [rate, indexes] of byRate) {
    const target = targets.get(rate) || { taxable: 0, inclusive: 0 }
    const nets = indexes.map((i) => items[i].net || 0)
    // ItemCalculatable#total_after_discount: net + the line's own rounded VAT, exact —
    // or, in gross_per_rate, the line's printed gross.
    const grosses = indexes.map((i, position) => items[i].gross ?? calculateItemGross(nets[position], rate, decimals))
    const categoryNet = nets.reduce((sum, net) => sum + net, 0)
    const categoryGross = grosses.reduce((sum, gross) => sum + gross, 0)
    let runningNet = 0
    let runningGross = 0

    indexes.forEach((itemIndex, position) => {
      if (position === indexes.length - 1) {
        shares[itemIndex] = {
          taxable: roundToDecimals(target.taxable - runningNet, decimals),
          inclusive: roundToDecimals(target.inclusive - runningGross, decimals)
        }
        return
      }
      const net = allocatedShare(nets[position], categoryNet, target.taxable, decimals)
      const gross = allocatedShare(grosses[position], categoryGross, target.inclusive, decimals)
      runningNet += net
      runningGross += gross
      shares[itemIndex] = { taxable: net, inclusive: gross }
    })
  }

  return shares.map(({ taxable, inclusive }) => ({ taxable, tax: roundToDecimals(inclusive - taxable, decimals) }))
}

function allocatedShare(amount, categoryAmount, targetAmount, decimals) {
  if (roundToDecimals(categoryAmount, decimals) === 0) return roundToDecimals(amount, decimals)
  return roundToDecimals(targetAmount * (amount / categoryAmount), decimals)
}

/**
 * Compute document-level discount amount.
 * @returns {number} the discount amount (positive)
 */
export function calculateDocumentDiscount(subtotal, percentage, amount, decimals = 2) {
  if (subtotal <= 0) return 0

  // Exact decimals like the server's BigDecimal: 16.90 × 15 is
  // 253.49999999999997 in floats, which rounded to 2.53 where the server says 2.54.
  if (percentage > 0) {
    return multiplyAndRound(subtotal, percentage, decimals)
  }
  if (amount > 0) {
    return Math.min(roundToDecimals(amount, decimals), subtotal)
  }
  return 0
}

/**
 * Full totals computation matching TotalsCalculatorService: the document
 * discount rounded to the currency, distributed per rate, VAT priced on the
 * discounted nets; plus the undiscounted VAT-inclusive total and the discount
 * with VAT the summary card prints. Pinned by totals-parity.test.js.
 *
 * `mode` is how the VAT is calculated, as the service's `mode:`.
 *   net_per_rate (default)  the line nets are summed per rate and the VAT added on top.
 *   gross_per_rate          the line grosses (each item's `gross`, which is required and
 *     replaces the net) are summed per rate, the document percentage is taken from that
 *     gross sum and split across the rates, and the VAT is carved out of what is left
 *     (carveTax). The discount shown (`discountAmount`) is what it took off the taxable
 *     bases. It takes a percentage only: an `amount` throws, the way the service refuses
 *     it, and so does an unknown mode. The summaries come out in ascending rate order.
 *
 * @param {Array<{net: number, gross?: number, taxRate: number}>} items - per-line rounded nets (BT-131) and
 *   rates; in gross_per_rate the rounded line grosses (calculateItemGrossLine) and rates
 * @param {{ percentage?: number, amount?: number }} documentDiscount - optional doc discount (the percent wins)
 * @param {number} decimals - currency decimals
 * @param {{ mode?: "net_per_rate" | "gross_per_rate" }} options
 * @returns {{ subtotalBeforeDiscount: number, discountAmount: number, subtotal: number, taxAmount: number,
 *   total: number, totalBeforeDocumentDiscount: number, discountWithVat: number, summaries: Array }}
 */
export function calculateTotals(items, documentDiscount = {}, decimals = 2, { mode = NET_PER_RATE } = {}) {
  if (mode === GROSS_PER_RATE) return calculateGrossTotals(items, documentDiscount, decimals)
  if (mode !== NET_PER_RATE) {
    throw new Error(`unknown mode ${JSON.stringify(mode)}, expected one of net_per_rate, gross_per_rate`)
  }

  // The server adds exact BigDecimal line nets; their float sum goes back on
  // the currency grid before the percent reads it — 0.30 + (−0.10) is
  // 0.19999999999999998 in floats, and 2.5 % of that rounded to 0.00 where
  // the server says 0.01.
  const subtotalBeforeDiscount = roundToDecimals(
    items.reduce((sum, item) => sum + item.net, 0),
    decimals
  )
  const discountAmount = calculateDocumentDiscount(
    subtotalBeforeDiscount,
    documentDiscount.percentage || 0,
    documentDiscount.amount || 0,
    decimals
  )
  const subtotal = roundToDecimals(subtotalBeforeDiscount - discountAmount, decimals)

  let adjustedItems = items
  if (discountAmount > 0 && subtotalBeforeDiscount > 0) {
    adjustedItems = distributeDocumentDiscount(items, discountAmount, subtotalBeforeDiscount, decimals)
  }

  const { totalTax, summaries } = calculateCategoryTax(adjustedItems, decimals)
  const total = roundToDecimals(subtotal + totalTax, decimals)
  // TotalsCalculatorService#total_before_document_discount: per-rate VAT on
  // the undiscounted nets; #document_discount_with_vat is the difference.
  const totalBeforeDocumentDiscount = roundToDecimals(
    subtotalBeforeDiscount + calculateCategoryTax(items, decimals).totalTax,
    decimals
  )

  return {
    subtotalBeforeDiscount,
    discountAmount,
    subtotal,
    taxAmount: totalTax,
    total,
    totalBeforeDocumentDiscount,
    discountWithVat: roundToDecimals(totalBeforeDocumentDiscount - total, decimals),
    summaries
  }
}

// TotalsCalculatorService in gross_per_rate. Every rate is one bucket holding the
// sum of its line grosses; the document percentage comes off those sums, and each
// bucket's VAT is carved out of what is left of it: inclusive = the bucket,
// tax = carveTax(inclusive), taxable = inclusive − tax.
function calculateGrossTotals(items, documentDiscount, decimals) {
  if ((documentDiscount.amount || 0) > 0) {
    throw new Error("gross_per_rate takes a document discount percentage only, not an amount")
  }

  const buckets = grossBuckets(items, decimals)
  const discountable = roundToDecimals(
    buckets.reduce((sum, { amount }) => sum + amount, 0),
    decimals
  )
  const discount = calculateDocumentDiscount(discountable, documentDiscount.percentage || 0, 0, decimals)
  const discounted = discount > 0 ? distributeGrossDiscount(buckets, discount, discountable, decimals) : buckets

  const before = buckets.map((bucket) => grossBucketFigures(bucket, decimals))
  const after = discounted.map((bucket) => grossBucketFigures(bucket, decimals))
  const sum = (rows, key) =>
    roundToDecimals(
      rows.reduce((total, row) => total + row[key], 0),
      decimals
    )

  const subtotalBeforeDiscount = sum(before, "taxable")
  const subtotal = sum(after, "taxable")
  const taxAmount = sum(after, "tax")
  const total = roundToDecimals(subtotal + taxAmount, decimals)
  const totalBeforeDocumentDiscount = sum(before, "inclusive")

  return {
    subtotalBeforeDiscount,
    discountAmount: roundToDecimals(subtotalBeforeDiscount - subtotal, decimals),
    subtotal,
    taxAmount,
    total,
    totalBeforeDocumentDiscount,
    discountWithVat: roundToDecimals(totalBeforeDocumentDiscount - total, decimals),
    summaries: after.map(({ rate, taxable, tax }) => ({ rate, taxable, tax }))
  }
}

// The line grosses summed per rate, in ascending rate order (the service sorts its
// breakdown the same way). The sum goes back on the currency grid: the server adds
// exact BigDecimal grosses, the floats' sum can be 16.299999999999997.
function grossBuckets(items, decimals) {
  const byRate = new Map()
  for (const { gross, taxRate } of items) {
    if (typeof gross !== "number") {
      throw new TypeError(`gross_per_rate needs the gross of every item, got ${typeof gross} ${String(gross)}`)
    }
    byRate.set(taxRate, (byRate.get(taxRate) || 0) + gross)
  }
  return [...byRate]
    .sort(([a], [b]) => (Number(a) || 0) - (Number(b) || 0))
    .map(([rate, amount]) => ({ rate, amount: roundToDecimals(amount, decimals) }))
}

function grossBucketFigures({ rate, amount }, decimals) {
  const tax = carveTax(amount, Number(rate) || 0, decimals)
  return { rate, inclusive: amount, tax, taxable: roundToDecimals(amount - tax, decimals) }
}

// TotalsCalculatorService#distribute_document_discount over the gross buckets: every
// rate but the last (the highest) takes its exact proportional share of the discount,
// rounded to the currency, and the last absorbs the rest, so the shares add up to it.
function distributeGrossDiscount(buckets, totalDiscount, totalAmount, decimals) {
  const discount = toDecimal(totalDiscount)
  const total = toDecimal(totalAmount)
  const last = buckets.length - 1
  let allocated = 0
  return buckets.map(({ rate, amount }, index) => {
    const share =
      index === last
        ? roundToDecimals(totalDiscount - allocated, decimals)
        : multiplyDivideAndRound(discount, toDecimal(amount), total, decimals)
    allocated += share
    return { rate, amount: roundToDecimals(amount - share, decimals) }
  })
}

/**
 * Distribute document discount proportionally across items, maintaining rate grouping.
 * Last item within each rate absorbs rounding remainder.
 */
export function distributeDocumentDiscount(items, totalDiscount, totalNet, decimals = 2) {
  const byRate = new Map()
  for (const item of items) {
    if (!byRate.has(item.taxRate)) byRate.set(item.taxRate, [])
    byRate.get(item.taxRate).push(item)
  }

  const result = []
  const sortedRates = [...byRate.keys()].sort((a, b) => a - b)
  let allocatedDiscount = 0

  for (let i = 0; i < sortedRates.length; i++) {
    const rate = sortedRates[i]
    const rateItems = byRate.get(rate)
    const categoryNet = rateItems.reduce((sum, item) => sum + item.net, 0)

    let categoryDiscount
    if (i === sortedRates.length - 1) {
      categoryDiscount = roundToDecimals(totalDiscount - allocatedDiscount, decimals)
    } else {
      categoryDiscount = roundToDecimals((totalDiscount * categoryNet) / totalNet, decimals)
      allocatedDiscount += categoryDiscount
    }

    const adjustedCategoryNet = categoryNet - categoryDiscount
    let allocatedNet = 0
    for (let j = 0; j < rateItems.length; j++) {
      const item = rateItems[j]
      let itemNet
      if (j === rateItems.length - 1) {
        // Last item absorbs remainder to guarantee sum = adjustedCategoryNet
        itemNet = roundToDecimals(adjustedCategoryNet - allocatedNet, decimals)
      } else {
        const share = categoryNet > 0 ? item.net / categoryNet : 0
        itemNet = roundToDecimals(adjustedCategoryNet * share, decimals)
        allocatedNet += itemNet
      }
      result.push({ net: itemNet, taxRate: item.taxRate })
    }
  }

  return result
}
